import { getSafeClient } from '@/integrations/supabase/safeClient';

// Helper function to get the base URL for API calls
const getBaseUrl = () => {
  return window.location.origin;
};

/**
 * API service for interacting with Stripe
 */
export const stripeApi = {
  /**
   * Create a Stripe Connect account for a seller
   */
  async createConnectAccount(businessType = 'individual', country = 'US') {
    try {
      const supabaseClient = await getSafeClient();
      const { data: { session } } = await supabaseClient.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${getBaseUrl()}/functions/v1/stripe-connect/create-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          business_type: businessType,
          country,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create Connect account');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating Connect account:', error);
      throw error;
    }
  },

  /**
   * Create an account link for onboarding
   */
  async createAccountLink(refreshUrl?: string, returnUrl?: string) {
    try {
      const supabaseClient = await getSafeClient();
      const { data: { session } } = await supabaseClient.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${getBaseUrl()}/functions/v1/stripe-connect/account-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          refresh_url: refreshUrl,
          return_url: returnUrl,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create account link');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating account link:', error);
      throw error;
    }
  },

  /**
   * Get the status of a Connect account
   */
  async getAccountStatus() {
    try {
      const supabaseClient = await getSafeClient();
      const { data: { session } } = await supabaseClient.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${getBaseUrl()}/functions/v1/stripe-connect/account-status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get account status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting account status:', error);
      throw error;
    }
  },

  /**
   * Get the onboarding status from the database
   */
  async getOnboardingStatus() {
    try {
      const supabaseClient = await getSafeClient();
      const { data: { session } } = await supabaseClient.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${getBaseUrl()}/functions/v1/stripe-connect/onboarding-status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get onboarding status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting onboarding status:', error);
      throw error;
    }
  },
};

export const createStripeConnectAccount = async (email: string) => {
  try {
    const response = await fetch(`${getBaseUrl()}/functions/v1/stripe-connect/create-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create Connect account');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating Connect account:', error);
    throw error;
  }
};

export const createAccountLink = async (accountId: string) => {
  try {
    const response = await fetch(`${getBaseUrl()}/functions/v1/stripe-connect/account-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ accountId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create account link');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating account link:', error);
    throw error;
  }
};

export const getAccountStatus = async (accountId: string) => {
  try {
    const response = await fetch(`${getBaseUrl()}/functions/v1/stripe-connect/account-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ accountId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get account status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting account status:', error);
    throw error;
  }
};

export const getOnboardingStatus = async (accountId: string) => {
  try {
    const response = await fetch(`${getBaseUrl()}/functions/v1/stripe-connect/onboarding-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ accountId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get onboarding status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting onboarding status:', error);
    throw error;
  }
};
